import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Theme, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { Edit as EditIcon } from "@mui/icons-material";
import { debounce, isEqual } from "lodash";

import DataGrid from "components/shared/XDataGrid";
import XNoRowsOverlay from "components/shared/XNoRowsOverlay";
import Pagination from "components/shared/Pagination";
import { ASSIGNED_USERS } from "helpers/apis";
import useAxios from "helpers/axios";
import useApp, { ProductLevel } from "hooks/useApp";
import useDataTable from "hooks/useXDataTable";
import { colors } from "theme";
import { IAssignedUser, UserStatus } from "types/user";

import { DetailLink } from "./DetailLink";
import { UserStatusBadge } from "./UserStatusBadge";

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    border: "none !important",
    fontSize: "12px !important",
    lineHeight: "17.5px !important",
    letterSpacing: "0.5px !important",
    "& .MuiDataGrid-columnHeaderTitle": {
      fontSize: 12,
      lineHeight: "17.5x",
      height: 56,
      letterSpacing: 0.5,
      color: colors.nero[300],
      display: "flex",
      alignItems: "center",
      whiteSpace: "break-spaces",
    },
  },
}));

export const DataTable = ({
  currentPage,
  filteredRows,
  setCurrentPage,
  setRowsData,
}: {
  currentPage: number;
  filteredRows: IAssignedUser[];
  setCurrentPage: Function;
  setRowsData: Function;
}) => {
  const [pageSize, setPageSize] = useState<number>(20);
  const [totalCount, setTotalCount] = useState<number>(0);
  const { productLevel } = useApp();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const columns: GridColDef[] = [
    {
      field: "status",
      sortable: false,
      hideSortIcons: true,
      headerName: "Status",
      flex: 1,
      type: "singleSelect",
      valueOptions: () => {
        return [
          { value: UserStatus.Pending, label: "Pending" },
          { value: UserStatus.Verified, label: "Verified" },
          { value: UserStatus.Decline, label: "Decline" },
          { value: UserStatus.Inactive, label: "Inactive" },
        ];
      },
      renderCell: ({ row }: GridRenderCellParams) => {
        return <UserStatusBadge status={row.status} />;
      },
    },
    {
      field: "id",
      sortable: false,
      headerName: "ID",
      flex: 1,
    },
    {
      field: "name",
      sortable: false,
      headerName: "Customer",
      flex: 2,
      valueGetter: ({ row }: GridValueGetterParams<IAssignedUser>) =>
        `${row.firstName} ${row.lastName}`,
    },
    {
      field: "email",
      sortable: false,
      headerName: "Email",
      flex: 2,
    },
    {
      field: "role",
      sortable: false,
      headerName: "Role",
      flex: 2,
      renderCell: ({ row }: GridRenderCellParams) => (
        <Box style={{ whiteSpace: "pre-wrap", textAlign: "left" }}>
          {row.role}
        </Box>
      ),
    },
    {
      field: "pendingRequests",
      sortable: false,
      headerName: "Pending Requests",
      align: "center",
      flex: 1,
      filterable: productLevel !== ProductLevel.MVP,
    },
    {
      field: "panelsOwned",
      sortable: false,
      headerName: "# Panels Owned",
      align: "center",
      flex: 1,
      filterable: productLevel !== ProductLevel.MVP,
    },
    {
      field: "action",
      sortable: false,
      headerName: " ",
      filterable: false,
      width: 46,
      renderCell: ({ row }: GridRenderCellParams) => (
        <DetailLink userInfo={row as IAssignedUser}>
          <EditIcon style={{ fontSize: 18 }} />
        </DetailLink>
      ),
    },
  ];
  const classes = useStyles();
  const { CustomToolbar, filter, filterButtonEl, setFilter } = useDataTable({
    columns,
  });

  const { fetchData, loading } = useAxios({
    config: { url: ASSIGNED_USERS },
    callOnInit: false,
    successCallback: ({ response }: any) => {
      if (Array.isArray(response.data)) {
        setRowsData(response.data);
        setTotalCount(response.totalCount ?? 0);
      } else {
        setRowsData([]);
        setTotalCount(0);
        setCurrentPage(1);
      }
    },
  });

  const { current: debouncedFetch } = useRef(
    debounce((currentPage, filter?: GridFilterItem) => {
      fetchData({
        params: {
          pageNumber: currentPage,
          pageSize: pageSize,
          ...(filter
            ? {
                filter: JSON.stringify({
                  id: filter.id,
                  column_field: filter.field,
                  operator_value: filter.operator,
                  value: filter.value,
                }),
              }
            : {}),
        },
      });
    }, 500)
  );

  const filterModelChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (!isEqual(filter, filterModel?.items)) {
        setFilter({ ...filterModel });
      }
    },
    [filter, setFilter]
  );

  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    if (debouncedFetch) {
      debouncedFetch(
        currentPage,
        filter.items?.[0]?.value !== undefined ? filter.items[0] : undefined
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFetch, currentPage, filter.items?.[0]]);

  return (
    <DataGrid
      autoHeight
      components={{
        NoRowsOverlay: () => <XNoRowsOverlay text="No Assigned Users" />,
        Pagination: () => (
          <Pagination
            totalCount={totalCount}
            pageSize={pageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        ),
        ColumnResizeIcon: () => <></>,
        Toolbar: CustomToolbar,
      }}
      componentsProps={{
        panel: {
          anchorEl: filterButtonEl,
        },
      }}
      columnVisibilityModel={{
        id: !isMobile,
        name: !isMobile,
        role: !isMobile,
        pendingRequests: !(productLevel === ProductLevel.MVP || isMobile),
        panelsOwned: !(productLevel === ProductLevel.MVP || isMobile),
      }}
      disableColumnMenu
      hideFooterSelectedRowCount
      hideFooterRowCount
      loading={loading}
      onPageSizeChange={(pageSize: number) => setPageSize(pageSize)}
      onPageChange={(page: number) => setCurrentPage(page)}
      page={currentPage - 1}
      pageSize={pageSize}
      rows={filteredRows}
      columns={columns}
      className={classes.table}
      onFilterModelChange={filterModelChange}
      filterModel={filter}
    />
  );
};
